import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getMe } from '../features/authSlice';
import { useStateContext } from "../contexts/ContextProvider";

import {
  FaTrash,
  FaSearch,
  FaTimesCircle,
  FaLeaf,
  FaComments,
  FaUser,
  FaTag,
  FaCalendarAlt,
  FaChevronDown,
  FaChevronRight,
} from "react-icons/fa";

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("id-ID", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

const ListComment = () => {
  const [allComments, setAllComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedStories, setExpandedStories] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedComment, setSelectedComment] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { currentColor, currentMode } = useStateContext();

  const isDark = currentMode === "Dark";
  const accentColor = currentColor || "#c9862a";
  const bgColor = isDark ? "#1a1a18" : "#f7f3ec";
  const cardBg = isDark ? "#242420" : "#ffffff";
  const borderColor = isDark ? "#2e2e28" : "#e8e0d0";
  const textPrimary = isDark ? "#f0ece3" : "#1a1a18";
  const textSecondary = isDark ? "#a09880" : "#6b6456";
  const inputBg = isDark ? "#1a1a18" : "#ffffff";
  const inputBorder = isDark ? "#3a3a34" : "#e8e0d0";

  useEffect(() => { dispatch(getMe()); }, [dispatch]);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (token) { fetchComments(); } else { navigate('/'); }
  }, [navigate, user]);

  const fetchComments = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const apiUrl = process.env.REACT_APP_URL_API;
      const response = await axios.get(`${apiUrl}/all-komentar`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllComments(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching comments:", error);
      setAllComments([]);
    } finally {
      setLoading(false);
    }
  };

  // ── Group by stories ─────────────────────────────────────────────────────
  const filteredByStories = allComments.reduce((groups, comment) => {
    const key = comment.storiesId;
    if (!groups[key]) {
      groups[key] = {
        storiesId: comment.storiesId,
        judul: comment.stories?.judul || "Stories #" + comment.storiesId,
        kategori: comment.stories?.kategori || "Article",
        image: comment.stories?.url || "",
        comments: [],
      };
    }
    groups[key].comments.push(comment);
    return groups;
  }, {});

  const storyGroups = Object.values(filteredByStories);

  // Apply search across group labels + comment text
  const searchedGroups = storyGroups.filter((group) => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    const hasMatch = group.comments.some(
      (c) =>
        (c.nama || "").toLowerCase().includes(q) ||
        (c.komentar || "").toLowerCase().includes(q) ||
        (group.judul || "").toLowerCase().includes(q)
    );
    return hasMatch;
  });

  // Auto-expand groups that match search
  useEffect(() => {
    if (searchTerm) {
      setExpandedStories((prev) => {
        const next = {};
        searchedGroups.forEach((g) => { next[g.storiesId] = true; });
        return { ...prev, ...next };
      });
    }
  }, [searchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleGroup = (id) =>
    setExpandedStories((prev) => ({ ...prev, [id]: !prev[id] }));

  // ── Delete logic ────────────────────────────────────────────────────────
  const handleDeleteClick = (comment) => {
    setSelectedComment(comment);
    setShowDeleteModal(true);
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setSelectedComment(null);
  };

  const handleDelete = async () => {
    if (!selectedComment) return;
    try {
      setDeletingId(selectedComment.id);
      const token = localStorage.getItem('accessToken');
      const apiUrl = process.env.REACT_APP_URL_API;
      await axios.delete(`${apiUrl}/komentar/${selectedComment.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllComments((prev) => prev.filter((c) => c.id !== selectedComment.id));
    } catch (error) {
      console.error("Error deleting comment:", error);
    } finally {
      setDeletingId(null);
      handleDeleteCancel();
    }
  };

  return (
    <div className="min-h-screen p-6" style={{ backgroundColor: bgColor }}>
      {/* Background deco */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute inset-0 opacity-5">
          <FaComments
            className="absolute bottom-40 left-20 text-7xl"
            style={{ color: accentColor }}
          />
          <FaLeaf
            className="absolute top-60 right-1/4 text-5xl"
            style={{ color: accentColor }}
          />
        </div>
      </div>

      <div className="relative z-10 mx-auto">

        {/* ── Header ── */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-8"
        >
          <div className="p-6 rounded-xl border" style={{ backgroundColor: cardBg, borderColor }}>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl" style={{ backgroundColor: accentColor }}>
                  <FaComments className="text-white text-2xl" />
                </div>
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-serif text-xs font-bold uppercase tracking-wider" style={{ color: textSecondary }}>
                      Komentar Management
                    </span>
                    <span className="text-xs" style={{ color: textSecondary }}>—</span>
                    <span className="font-serif text-xs font-bold" style={{ color: accentColor }}>
                      Rumoh Pangan Aceh
                    </span>
                  </div>
                  <h1 className="text-2xl font-serif font-bold" style={{ color: textPrimary }}>
                    Data Komentar
                  </h1>
                  <p className="text-sm mt-1" style={{ color: textSecondary }}>
                    Kelola komentar dari setiap stories
                  </p>
                </div>
              </div>
              {/* Stat pills */}
              <div className="flex gap-3">
                <div
                  className="flex items-center gap-2 px-4 py-2 rounded-xl border"
                  style={{ backgroundColor: `${accentColor}10`, borderColor: `${accentColor}30` }}
                >
                  <FaComments className="text-sm" style={{ color: accentColor }} />
                  <span className="text-sm font-bold" style={{ color: accentColor }}>{allComments.length}</span>
                  <span className="text-xs" style={{ color: textSecondary }}>Total</span>
                </div>
                <div
                  className="flex items-center gap-2 px-4 py-2 rounded-xl border"
                  style={{ backgroundColor: isDark ? "#2e2e28" : "#f0ece3", borderColor }}
                >
                  <FaTag className="text-sm" style={{ color: textSecondary }} />
                  <span className="text-sm font-bold" style={{ color: textPrimary }}>{storyGroups.length}</span>
                  <span className="text-xs" style={{ color: textSecondary }}>Stories</span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* ── Search ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-6"
        >
          <div className="p-4 rounded-xl border" style={{ backgroundColor: cardBg, borderColor }}>
            <div className="relative">
              <FaSearch
                className="absolute left-4 top-1/2 transform -translate-y-1/2 text-sm"
                style={{ color: textSecondary }}
              />
              <input
                type="text"
                placeholder="Cari berdasarkan nama atau isi komentar..."
                className="w-full pl-11 pr-4 py-3 rounded-lg border transition-all duration-300 outline-none"
                style={{ backgroundColor: inputBg, borderColor: inputBorder, color: textPrimary }}
                onFocus={(e) => (e.target.style.borderColor = accentColor)}
                onBlur={(e) => (e.target.style.borderColor = inputBorder)}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </motion.div>

        {/* ── Comment Groups ── */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2].map((i) => (
              <div key={i} className="h-24 rounded-xl animate-pulse border" style={{ backgroundColor: cardBg, borderColor }} />
            ))}
          </div>
        ) : searchedGroups.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="py-24 text-center rounded-xl border"
            style={{ backgroundColor: cardBg, borderColor }}
          >
            <FaComments className="text-6xl mb-4 mx-auto opacity-30" style={{ color: textSecondary }} />
            <h3 className="text-lg font-semibold mb-2" style={{ color: textPrimary }}>
              {searchTerm ? "Komentar tidak ditemukan" : "Belum ada komentar"}
            </h3>
            <p className="text-sm" style={{ color: textSecondary }}>
              {searchTerm ? "Coba ubah kata kunci pencarian" : "Komentar akan muncul setelah ada pengunjung yang berkomentar"}
            </p>
          </motion.div>
        ) : (
          <div className="space-y-5">
            {searchedGroups.map((group, gi) => {
              const isExpanded = !!expandedStories[group.storiesId];
              return (
                <motion.div
                  key={group.storiesId}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: gi * 0.06 }}
                  className="rounded-xl border overflow-hidden"
                  style={{ backgroundColor: cardBg, borderColor }}
                >
                  {/* ── Stories Header (click to expand) ── */}
                  <div
                    className="flex items-center gap-4 p-5 cursor-pointer transition-colors duration-200 select-none"
                    style={
                      isExpanded
                        ? { borderBottom: `1px solid ${borderColor}` }
                        : {}
                    }
                    onClick={() => toggleGroup(group.storiesId)}
                  >
                    {/* Thumbnail */}
                    <div
                      className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0 border"
                      style={{ borderColor }}
                    >
                      {group.image ? (
                        <img
                          src={group.image}
                          alt={group.judul}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = "none";
                            e.target.parentElement.style.backgroundColor = `${accentColor}20`;
                          }}
                        />
                      ) : (
                        <div
                          className="w-full h-full flex items-center justify-center"
                          style={{ backgroundColor: `${accentColor}20` }}
                        >
                          <FaComments className="text-lg" style={{ color: accentColor }} />
                        </div>
                      )}
                    </div>

                    {/* Stories info */}
                    <div className="flex-1 min-w-0">
                      <h3
                        className="font-serif font-bold text-base leading-snug mb-1 truncate"
                        style={{ color: textPrimary }}
                      >
                        {group.judul}
                      </h3>
                      <div className="flex flex-wrap items-center gap-3">
                        <div className="flex items-center gap-1.5">
                          <FaTag className="text-xs" style={{ color: accentColor }} />
                          <span className="text-xs font-medium" style={{ color: accentColor }}>
                            {group.kategori}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <FaComments className="text-xs" style={{ color: textSecondary }} />
                          <span className="text-xs font-semibold" style={{ color: textSecondary }}>
                            {group.comments.length} Komentar
                          </span>
                        </div>
                        {group.comments[0] && (
                          <div className="flex items-center gap-1.5">
                            <FaCalendarAlt className="text-xs" style={{ color: textSecondary }} />
                            <span className="text-xs" style={{ color: textSecondary }}>
                              {formatDate(group.comments[0].createdAt)}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Chevron */}
                    <div
                      className="flex-shrink-0 p-2 rounded-lg transition-all duration-300"
                      style={{ color: textSecondary }}
                    >
                      {isExpanded ? (
                        <FaChevronDown className="text-sm" />
                      ) : (
                        <FaChevronRight className="text-sm" />
                      )}
                    </div>
                  </div>

                  {/* ── Comment Items (animated) ── */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <div className="divide-y" style={{ borderColor }}>
                          {group.comments
                            .filter((c) => {
                              if (!searchTerm) return true;
                              const q = searchTerm.toLowerCase();
                              return (
                                (c.nama || "").toLowerCase().includes(q) ||
                                (c.komentar || "").toLowerCase().includes(q)
                              );
                            })
                            .map((comment, ci) => (
                              <motion.div
                                key={comment.id}
                                initial={{ opacity: 0, x: -8 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.25, delay: ci * 0.04 }}
                                className="flex items-start gap-4 p-5 group hover:bg-opacity-50 transition-colors duration-200"
                                style={
                                  ci % 2 === 0
                                    ? { backgroundColor: isDark ? "rgba(0,0,0,0.1)" : "#faf8f4" }
                                    : { backgroundColor: isDark ? "rgba(0,0,0,0)" : "#ffffff" }
                                }
                              >

                                {/* Comment content */}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center justify-between gap-3 flex-wrap">
                                    <div className="flex items-center gap-2">
                                      <FaUser className="text-xs" style={{ color: accentColor }} />
                                      <span className="font-semibold text-sm" style={{ color: textPrimary }}>
                                        {comment.nama}
                                      </span>
                                      <div className="w-1 h-1 rounded-full" style={{ backgroundColor: borderColor }} />
                                      <span className="text-xs" style={{ color: textSecondary }}>
                                        {formatDate(comment.createdAt)}
                                      </span>
                                    </div>

                                    {/* Delete button */}
                                    <button
                                      onClick={() => handleDeleteClick(comment)}
                                      disabled={deletingId === comment.id}
                                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium opacity-0 group-hover:opacity-100 transition-all duration-200 hover:scale-105 disabled:opacity-50"
                                      style={{
                                        backgroundColor: "#fef2f2",
                                        color: "#dc2626",
                                      }}
                                      title="Hapus komentar"
                                    >
                                      {deletingId === comment.id ? (
                                        <span className="w-3 h-3 border-2 border-red-300 border-t-red-600 rounded-full animate-spin inline-block" />
                                      ) : (
                                        <FaTrash className="text-xs" />
                                      )}
                                      Hapus
                                    </button>
                                  </div>

                                  {/* Comment text */}
                                  <p
                                    className="mt-2 text-sm leading-relaxed whitespace-pre-wrap"
                                    style={{ color: textSecondary }}
                                  >
                                    {comment.komentar}
                                  </p>
                                </div>
                              </motion.div>
                            ))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Delete Confirmation Modal ── */}
      {showDeleteModal && selectedComment && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            onClick={handleDeleteCancel}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className="relative w-full max-w-md mx-auto rounded-2xl shadow-2xl overflow-hidden z-10"
            style={{ backgroundColor: cardBg, border: `1px solid ${borderColor}` }}
          >
            {/* Modal header */}
            <div
              className="px-6 py-4 flex items-center gap-3"
              style={{ background: "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)" }}
            >
              <FaTimesCircle className="h-6 w-6 text-white" />
              <h3 className="text-lg font-bold font-serif text-white">Hapus Komentar</h3>
            </div>

            {/* Modal body */}
            <div className="px-6 py-6">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-full flex-shrink-0" style={{ backgroundColor: "#fef2f2" }}>
                  <FaTrash className="h-6 w-6 text-red-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm leading-relaxed mb-3" style={{ color: textPrimary }}>
                    Hapus komentar dari{" "}
                    <strong>{selectedComment.nama}</strong>?
                  </p>
                  <p
                    className="text-xs italic leading-relaxed p-3 rounded-lg border"
                    style={{
                      backgroundColor: isDark ? "#1a1a18" : "#f7f3ec",
                      borderColor,
                      color: textSecondary,
                    }}
                  >
                    "{selectedComment.komentar}"
                  </p>
                </div>
              </div>
            </div>

            {/* Modal footer */}
            <div
              className="px-6 py-4 border-t flex gap-3 justify-end"
              style={{ backgroundColor: isDark ? "#1a1a18" : "#f7f3ec", borderColor }}
            >
              <button
                type="button"
                onClick={handleDeleteCancel}
                className="px-5 py-2.5 border rounded-xl text-sm font-medium transition-all duration-300"
                style={{ borderColor, color: textPrimary, backgroundColor: cardBg }}
              >
                Batal
              </button>
              <button
                type="button"
                onClick={handleDelete}
                className="px-5 py-2.5 rounded-xl text-sm font-medium text-white transition-all duration-300 hover:shadow-lg"
                style={{ backgroundColor: "#dc2626" }}
              >
                Hapus Komentar
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default ListComment;